import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Loader2, ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";

const ACTIVITY_TYPES = [
  { slug: "family",  label: "Family" },
  { slug: "free",    label: "Free" },
  { slug: "outdoor", label: "Outdoor" },
  { slug: "action",  label: "Action" },
  { slug: "ocean",   label: "Ocean" },
  { slug: "tour",    label: "Tour" },
  { slug: "scenic",  label: "Scenic" },
  { slug: "eco",     label: "Eco / conservation" },
  { slug: "other",   label: "Other" },
];

export default function SubmitTour() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);
  const [ownedOperators, setOwnedOperators] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    activity_type: "sail",
    operator_venue_id: "",
    price_from: "",
    currency: "AUD",
    duration_label: "",
    image: "",
    booking_url: "",
    highlights: [""],
  });
  const [submitting, setSubmitting] = useState(false);
  const [loadingTour, setLoadingTour] = useState(isEditing);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/my/venues").then((r) => {
      const ops = r.data.filter((v) => v.category === "activity");
      setOwnedOperators(ops);
      if (ops.length && !form.operator_venue_id && !isEditing) {
        setForm((f) => ({ ...f, operator_venue_id: ops[0].id }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // In edit mode, fetch the existing tour and populate the form once.
  useEffect(() => {
    if (!isEditing) return;
    api.get(`/tours/${editId}`).then((r) => {
      const t = r.data;
      setForm({
        title: t.title || "",
        description: t.description || "",
        activity_type: t.activity_type || "sail",
        operator_venue_id: t.operator_venue_id || "",
        price_from: String(t.price_from ?? ""),
        currency: t.currency || "AUD",
        duration_label: t.duration_label || "",
        image: t.image || "",
        booking_url: t.booking_url || "",
        highlights: (t.highlights && t.highlights.length) ? t.highlights : [""],
      });
    }).catch(() => toast.error("Could not load tour"))
      .finally(() => setLoadingTour(false));
  }, [editId, isEditing]);

  const setHighlight = (i, v) => {
    setForm((f) => {
      const copy = [...f.highlights];
      copy[i] = v;
      return { ...f, highlights: copy };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.operator_venue_id) {
      toast.error("Pick an operator first.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price_from: parseFloat(form.price_from || 0),
        highlights: form.highlights.map((h) => h.trim()).filter(Boolean),
      };
      if (isEditing) {
        await api.put(`/tours/${editId}`, payload);
        toast.success("Tour updated.");
      } else {
        await api.post("/tours", payload);
        toast.success("Tour published — it's now live on the Play page.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEditing ? "Could not update tour" : "Could not publish tour"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;
  if (loadingTour) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-20 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-navy" />
        </div>
      </Layout>
    );
  }

  if (ownedOperators.length === 0) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-3xl text-navy mb-3">No operator venue yet</h1>
          <p className="text-neutral-500 mb-6">
            Tours can only be added by the owner of an <strong>activity / tour operator</strong> venue.
            Submit your operator first — or if you already manage one and want it transferred to your account,
            use the &quot;Is this your venue?&quot; button on the venue page.
          </p>
          <Link to="/dashboard/submit">
            <Button className="rounded-full bg-navy hover:bg-navy/90" data-testid="submit-venue-cta">
              Submit my operator venue
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="text-sm text-neutral-500 inline-flex items-center gap-1 hover:text-navy" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <h1 className="font-heading text-4xl text-navy tracking-tight mt-4 mb-2">{isEditing ? "Edit tour" : "Submit an Activity"}</h1>
        <p className="text-neutral-500 mb-10">{isEditing ? "Update the details and re-publish." : "Add a bookable experience to inAirlie. Visitors find it under the Activities section."}</p>

        <form onSubmit={submit} className="space-y-6 bg-white border border-black/5 rounded-2xl p-8" data-testid="submit-tour-form">
          <Field label="Operator">
            <select
              value={form.operator_venue_id}
              onChange={(e) => setForm({ ...form, operator_venue_id: e.target.value })}
              className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white"
              required
              data-testid="field-operator"
            >
              {ownedOperators.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>

          <Field label="Tour title">
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Whitehaven Beach Half Day"
              required
              data-testid="field-title"
            />
          </Field>

          <Field label="Description">
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's the experience? Who's it perfect for?"
              rows={4}
              required
              data-testid="field-description"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Activity type">
              <select
                value={form.activity_type}
                onChange={(e) => setForm({ ...form, activity_type: e.target.value })}
                className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white"
                data-testid="field-activity-type"
              >
                {ACTIVITY_TYPES.map((a, i) => (
                  <option key={`${a.slug}-${i}`} value={a.slug}>{a.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Duration">
              <Input
                value={form.duration_label}
                onChange={(e) => setForm({ ...form, duration_label: e.target.value })}
                placeholder="e.g. Full day · 8 hours"
                required
                data-testid="field-duration"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Price from">
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.price_from}
                  onChange={(e) => setForm({ ...form, price_from: e.target.value })}
                  placeholder="189"
                  required
                  data-testid="field-price"
                />
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="rounded-lg border border-black/10 px-2 bg-white text-sm"
                  data-testid="field-currency"
                >
                  <option>AUD</option><option>USD</option><option>EUR</option>
                </select>
              </div>
            </Field>

            <Field label="Booking URL">
              <Input
                type="text"
                inputMode="url"
                value={form.booking_url}
                onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && !/^https?:\/\//i.test(v)) {
                    setForm((f) => ({ ...f, booking_url: `https://${v}` }));
                  }
                }}
                placeholder="yourtour.com/book"
                data-testid="field-booking-url"
              />
            </Field>
          </div>

          <Field label="Cover image URL">
            <Input
              type="text"
              inputMode="url"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && !/^https?:\/\//i.test(v)) {
                  setForm((f) => ({ ...f, image: `https://${v}` }));
                }
              }}
              placeholder="example.com/photo.jpg (we recommend 1200×800)"
              data-testid="field-image"
            />
          </Field>

          <Field label="Highlights (one per line — short bullet points)">
            <div className="space-y-2">
              {form.highlights.map((h, i) => (
                // Controlled input bound to array index — value prop owns state,
                // so index key is safe & canonical here. Re-ordering would
                // re-render the value, not lose it.
                // eslint-disable-next-line react/no-array-index-key
                <div key={`hl-${i}`} className="flex gap-2">
                  <Input
                    value={h}
                    onChange={(e) => setHighlight(i, e.target.value)}
                    placeholder="e.g. Whitehaven Beach"
                    data-testid={`field-highlight-${i}`}
                  />
                  {form.highlights.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setForm((f) => ({ ...f, highlights: f.highlights.filter((_, idx) => idx !== i) }))}
                      className="rounded-lg"
                      data-testid={`remove-highlight-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => setForm((f) => ({ ...f, highlights: [...f.highlights, ""] }))}
                className="rounded-lg text-sm"
                data-testid="add-highlight"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add another
              </Button>
            </div>
          </Field>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-navy hover:bg-navy/90 h-11"
            data-testid="submit-tour-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEditing ? "Save changes" : "Publish tour")}
          </Button>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-neutral-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}
